<template>
  <div class="troubleshooting-tab">
    <div class="panel">
      <div class="panel-header">
        <div>
          <h3>Automation health</h3>
          <p class="panel-subtitle">
            See if ServiceM8 is still sending updates to your {{ brand.agencyName }} workflows.
            You can check now instead of waiting for the daily review.
          </p>
        </div>
        <div class="panel-actions">
          <button
            type="button"
            class="secondary-btn"
            :disabled="loadingStatus || checking"
            @click="loadStatus"
          >
            {{ loadingStatus ? 'Refreshing…' : 'Refresh' }}
          </button>
          <button
            type="button"
            class="primary-btn"
            :disabled="loadingStatus || checking || !status.connected"
            @click="runCheck"
          >
            {{ checking ? 'Checking…' : 'Check now' }}
          </button>
        </div>
      </div>

      <p
        v-if="friendlyBanner"
        class="banner"
        :class="`banner-${bannerStatus}`"
      >
        {{ friendlyBanner }}
      </p>

      <div v-if="loadingStatus && !status.locationId" class="loading">Checking connection…</div>
      <div v-else class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">ServiceM8</div>
          <div class="stat-value" :class="status.connected ? 'ok' : 'bad'">
            {{ status.connected ? 'Connected' : 'Not connected' }}
          </div>
          <div class="stat-meta">{{ status.accountName || 'Open Integration → Connections if this looks wrong' }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Workflows</div>
          <div class="stat-value">{{ status.triggerCount || 0 }}</div>
          <div class="stat-meta">
            {{ status.healthyCount || 0 }} working
            <span v-if="status.brokenCount"> · {{ status.brokenCount }} need attention</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Last checked</div>
          <div class="stat-value small">{{ formatFriendlyDate(status.webhookLastCheckedAt) }}</div>
          <div class="stat-meta">Connected {{ formatFriendlyDate(status.connectedAt) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Next automatic check</div>
          <div class="stat-value small">{{ status.checkDue ? 'Due now' : formatFriendlyDate(status.nextCheckAt) }}</div>
          <div class="stat-meta">Once a day after this account was connected</div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-header">
        <div>
          <h3>Your workflows</h3>
          <p class="panel-subtitle">
            Each row is a workflow that should receive ServiceM8 updates. Filter by when a problem last happened.
          </p>
        </div>
      </div>

      <div v-if="!status.connected" class="empty">
        Connect ServiceM8 in Integration → Connections first.
      </div>
      <div v-else-if="(status.webhooks || []).length === 0" class="empty">
        No workflows are set up yet. Add a {{ brand.agencyName }} workflow trigger, then come back here.
      </div>
      <template v-else>
        <div class="filter-bar">
          <label>
            When a problem last happened
            <select v-model="webhookDatePreset" @change="applyWebhookDatePreset">
              <option value="all">Any time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Choose dates</option>
            </select>
          </label>
          <template v-if="webhookDatePreset === 'custom'">
            <label>
              From
              <input v-model="webhookDateFrom" type="date" />
            </label>
            <label>
              To
              <input v-model="webhookDateTo" type="date" />
            </label>
          </template>
          <label>
            Status
            <select v-model="webhookStatusFilter">
              <option value="">All</option>
              <option value="active">Working</option>
              <option value="inactive">Stopped</option>
              <option value="missing">Missing</option>
            </select>
          </label>
        </div>
        <p class="hint">{{ filteredWebhooks.length }} of {{ status.webhooks.length }} workflows shown.</p>

        <div v-if="filteredWebhooks.length === 0" class="empty">
          No workflows match that date or status.
        </div>
        <div v-else class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>What it watches</th>
                <th>Last problem</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="hook in filteredWebhooks"
                :key="hook.triggerId"
                :class="{ selected: selectedWebhookId === hook.triggerId }"
                @click="selectedWebhookId = selectedWebhookId === hook.triggerId ? null : hook.triggerId"
              >
                <td>
                  <span class="status-badge" :class="`status-${hook.status}`">
                    {{ formatWebhookStatus(hook.status) }}
                  </span>
                </td>
                <td>
                  <div class="primary-text">{{ formatEventName(hook.sm8Event) }}</div>
                  <div class="muted">{{ formatTriggerName(hook.triggerKey) }}</div>
                </td>
                <td>
                  <div class="primary-text">{{ hook.lastFailureReason || 'None recorded' }}</div>
                  <div class="muted">{{ formatFriendlyDate(hook.lastFailureAt) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="selectedWebhook" class="detail-panel">
          <strong>{{ formatEventName(selectedWebhook.sm8Event) }}</strong>
          <p class="detail-line">Status: {{ formatWebhookStatus(selectedWebhook.status) }}</p>
          <p v-if="selectedWebhook.lastFailureReason" class="detail-line">
            Last problem: {{ selectedWebhook.lastFailureReason }}
          </p>
          <p class="hint">Click the row again to hide this.</p>
        </div>
      </template>
    </div>

    <div class="panel">
      <div class="panel-header">
        <div>
          <h3>Recent activity</h3>
          <p class="panel-subtitle">
            A simple history of workflow actions, contact syncs, and automatic checks.
          </p>
        </div>
        <button
          type="button"
          class="secondary-btn"
          :disabled="loadingMonitor"
          @click="loadMonitor"
        >
          {{ loadingMonitor ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>

      <div class="monitor-tabs">
        <button
          type="button"
          :class="['monitor-tab', { active: monitorTab === 'actions' }]"
          @click="monitorTab = 'actions'"
        >
          Actions
          <span class="tab-count">{{ actionRows.length }}</span>
        </button>
        <button
          type="button"
          :class="['monitor-tab', { active: monitorTab === 'contacts' }]"
          @click="monitorTab = 'contacts'"
        >
          Contacts
          <span class="tab-count">{{ contactRows.length }}</span>
        </button>
        <button
          type="button"
          :class="['monitor-tab', { active: monitorTab === 'webhooks' }]"
          @click="monitorTab = 'webhooks'"
        >
          Automatic checks
          <span class="tab-count">{{ webhookRows.length }}</span>
        </button>
      </div>

      <div class="filter-bar">
        <label>
          Date
          <select v-model="filters.datePreset" @change="applyActivityDatePreset">
            <option value="all">Any time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Choose dates</option>
          </select>
        </label>
        <template v-if="filters.datePreset === 'custom'">
          <label>
            From
            <input v-model="filters.dateFrom" type="date" />
          </label>
          <label>
            To
            <input v-model="filters.dateTo" type="date" />
          </label>
        </template>
        <label>
          Result
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="success">Worked</option>
            <option value="failed">Had a problem</option>
          </select>
        </label>
        <label v-if="monitorTab !== 'webhooks'">
          Direction
          <select v-model="filters.direction">
            <option value="">Both ways</option>
            <option value="ghl_to_sm8">{{ brand.agencyName }} to ServiceM8</option>
            <option value="sm8_to_ghl">ServiceM8 to {{ brand.agencyName }}</option>
          </select>
        </label>
        <label class="filter-search">
          Search
          <input
            v-model.trim="filters.query"
            type="search"
            :placeholder="searchPlaceholder"
          />
        </label>
      </div>

      <p class="hint">
        {{ filteredMonitorRows.length }}
        {{ filteredMonitorRows.length === 1 ? 'item' : 'items' }}
        · {{ monitorSuccessCount }} worked
        <span v-if="monitorFailedCount"> · {{ monitorFailedCount }} had a problem</span>
      </p>

      <div v-if="loadingMonitor" class="loading">Loading activity…</div>
      <div v-else-if="filteredMonitorRows.length === 0" class="empty">
        Nothing matches these filters. Try a wider date range.
      </div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>What happened</th>
              <th v-if="monitorTab !== 'webhooks'">Direction</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredMonitorRows"
              :key="item.id"
              :class="{ selected: selectedMonitorId === item.id }"
              @click="selectedMonitorId = selectedMonitorId === item.id ? null : item.id"
            >
              <td class="nowrap">{{ formatFriendlyDate(item.syncedAt) }}</td>
              <td>{{ item.errorMessage || formatSyncType(item.syncType) }}</td>
              <td v-if="monitorTab !== 'webhooks'" class="nowrap muted">
                {{ formatDirection(item.direction) }}
              </td>
              <td>
                <span class="status-badge" :class="`status-${item.status === 'success' ? 'active' : 'inactive'}`">
                  {{ item.status === 'success' ? 'Worked' : 'Problem' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="selectedMonitorItem" class="detail-panel">
        <strong>{{ selectedMonitorItem.errorMessage || formatSyncType(selectedMonitorItem.syncType) }}</strong>
        <p class="detail-line">When: {{ formatFriendlyDate(selectedMonitorItem.syncedAt) }}</p>
        <p class="detail-line">Result: {{ selectedMonitorItem.status === 'success' ? 'Worked' : 'Had a problem' }}</p>
        <p v-if="monitorTab !== 'webhooks'" class="detail-line">
          Direction: {{ formatDirection(selectedMonitorItem.direction) }}
        </p>
        <p class="hint">Click the row again to hide this.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { brandMixin } from '../brandMixin'

const ACTION_TYPES = ['action_started', 'action_executed'];
const CONTACT_TYPES = ['contact_sync'];
const WEBHOOK_TYPES = ['webhook_monitor'];

function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfDay(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function endOfDay(value) {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function presetRange(preset) {
  const now = new Date();
  if (preset === 'today') {
    return { from: toDateInput(now), to: toDateInput(now) };
  }
  if (preset === '7d') {
    const from = new Date(now);
    from.setDate(now.getDate() - 6);
    return { from: toDateInput(from), to: toDateInput(now) };
  }
  if (preset === '30d') {
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    return { from: toDateInput(from), to: toDateInput(now) };
  }
  return { from: '', to: '' };
}

function isInDateRange(value, from, to) {
  if (!from && !to) return true;
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const start = startOfDay(from);
  const end = endOfDay(to);
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
}

export default {
  name: 'TroubleshootingTab',
  mixins: [brandMixin],
  data() {
    return {
      loadingStatus: false,
      checking: false,
      loadingMonitor: false,
      bannerMessage: '',
      bannerStatus: 'info',
      monitorTab: 'actions',
      selectedMonitorId: null,
      selectedWebhookId: null,
      webhookDatePreset: 'all',
      webhookDateFrom: '',
      webhookDateTo: '',
      webhookStatusFilter: '',
      filters: {
        status: '',
        direction: '',
        query: '',
        datePreset: '30d',
        dateFrom: '',
        dateTo: '',
      },
      status: {
        locationId: '',
        connected: false,
        accountName: null,
        accountUUID: null,
        connectedAt: null,
        webhookLastCheckedAt: null,
        nextCheckAt: null,
        checkDue: false,
        triggerCount: 0,
        healthyCount: 0,
        brokenCount: 0,
        listError: null,
        message: '',
        webhooks: [],
      },
      actionRows: [],
      contactRows: [],
      webhookRows: [],
    };
  },
  computed: {
    searchPlaceholder() {
      if (this.monitorTab === 'contacts') return 'Search contacts or errors';
      if (this.monitorTab === 'webhooks') return 'Search check results';
      return 'Search actions or errors';
    },
    friendlyBanner() {
      return this.humanizeBanner(this.bannerMessage);
    },
    currentMonitorRows() {
      if (this.monitorTab === 'contacts') return this.contactRows;
      if (this.monitorTab === 'webhooks') return this.webhookRows;
      return this.actionRows;
    },
    filteredWebhooks() {
      return (this.status.webhooks || []).filter((hook) => {
        if (this.webhookStatusFilter && hook.status !== this.webhookStatusFilter) return false;
        if (this.webhookDatePreset === 'all') return true;
        return isInDateRange(hook.lastFailureAt, this.webhookDateFrom, this.webhookDateTo);
      });
    },
    filteredMonitorRows() {
      const query = this.filters.query.toLowerCase();
      return this.currentMonitorRows.filter((item) => {
        if (this.filters.status && item.status !== this.filters.status) return false;
        if (this.monitorTab !== 'webhooks' && this.filters.direction && item.direction !== this.filters.direction) {
          return false;
        }
        if (this.filters.datePreset !== 'all' && !isInDateRange(item.syncedAt, this.filters.dateFrom, this.filters.dateTo)) {
          return false;
        }
        if (!query) return true;
        const haystack = [
          item.syncType,
          item.direction,
          item.status,
          item.errorMessage,
          this.formatSyncType(item.syncType),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    },
    monitorSuccessCount() {
      return this.filteredMonitorRows.filter((item) => item.status === 'success').length;
    },
    monitorFailedCount() {
      return this.filteredMonitorRows.filter((item) => item.status === 'failed').length;
    },
    selectedMonitorItem() {
      if (!this.selectedMonitorId) return null;
      return this.filteredMonitorRows.find((item) => item.id === this.selectedMonitorId) || null;
    },
    selectedWebhook() {
      if (!this.selectedWebhookId) return null;
      return this.filteredWebhooks.find((hook) => hook.triggerId === this.selectedWebhookId) || null;
    },
  },
  watch: {
    monitorTab() {
      this.selectedMonitorId = null;
    },
  },
  created() {
    this.applyActivityDatePreset();
  },
  async mounted() {
    await Promise.all([this.loadStatus(), this.loadMonitor()]);
  },
  methods: {
    applyWebhookDatePreset() {
      if (this.webhookDatePreset === 'custom') return;
      const range = presetRange(this.webhookDatePreset);
      this.webhookDateFrom = range.from;
      this.webhookDateTo = range.to;
    },
    applyActivityDatePreset() {
      if (this.filters.datePreset === 'custom') return;
      const range = presetRange(this.filters.datePreset);
      this.filters.dateFrom = range.from;
      this.filters.dateTo = range.to;
    },
    applyStatus(payload) {
      this.status = {
        ...this.status,
        ...payload,
        webhooks: Array.isArray(payload.webhooks) ? payload.webhooks : [],
      };
    },
    humanizeBanner(message) {
      if (!message) return '';
      const text = String(message);
      if (/no ghl trigger webhooks/i.test(text)) {
        return 'No workflows are registered for this location yet.';
      }
      if (/all are active|all active/i.test(text)) {
        return 'All workflows look healthy.';
      }
      if (/re-registered/i.test(text)) {
        return text.replace(/webhook\(s\)/gi, 'workflow(s)').replace(/Re-registered/i, 'Repaired');
      }
      if (/not connected/i.test(text)) {
        return 'ServiceM8 is not connected. Open Integration → Connections to connect it.';
      }
      return text;
    },
    formatWebhookStatus(status) {
      if (status === 'active') return 'Working';
      if (status === 'inactive') return 'Stopped';
      if (status === 'missing') return 'Missing';
      return status || 'Unknown';
    },
    formatEventName(event) {
      if (!event) return 'Workflow';
      return String(event)
        .replace(/[._]/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    formatTriggerName(key) {
      if (!key) return '';
      return String(key)
        .replace(/^sm8/i, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .trim();
    },
    async loadStatus() {
      this.loadingStatus = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/webhooks/status', {
          method: 'GET',
          headers: { 'x-sso-token': ssoToken },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok) {
          this.applyStatus(payload);
          this.bannerMessage = payload.listError || payload.message || '';
          this.bannerStatus = payload.connected
            ? (payload.brokenCount > 0 || payload.listError ? 'warn' : 'info')
            : 'error';
        } else {
          this.bannerStatus = 'error';
          this.bannerMessage = payload.message || payload.error || 'Could not load workflow status.';
        }
      } catch (error) {
        console.error('Error loading webhook status:', error);
        this.bannerStatus = 'error';
        this.bannerMessage = 'Could not load workflow status.';
      } finally {
        this.loadingStatus = false;
      }
    },
    async runCheck() {
      this.checking = true;
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/webhooks/troubleshoot', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-sso-token': ssoToken,
          },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.ok) {
          this.applyStatus(payload);
          this.bannerStatus = payload.failed > 0 ? 'error' : 'success';
          this.bannerMessage = payload.message || 'Check finished.';
        } else {
          this.bannerStatus = 'error';
          this.bannerMessage = payload.message || payload.error || 'Could not check workflows.';
        }
        await this.loadMonitor();
        this.monitorTab = 'webhooks';
      } catch (error) {
        console.error('Error checking webhooks:', error);
        this.bannerStatus = 'error';
        this.bannerMessage = 'Could not check workflows.';
      } finally {
        this.checking = false;
      }
    },
    async fetchHistory(syncTypes) {
      const ssoToken = await this.getSSOToken();
      const params = new URLSearchParams({
        limit: '100',
        syncTypes: syncTypes.join(','),
      });
      const response = await fetch(`/servicem8/sync/history?${params.toString()}`, {
        method: 'GET',
        headers: { 'x-sso-token': ssoToken },
      });
      if (!response.ok) return [];
      const rows = await response.json();
      return Array.isArray(rows) ? rows : [];
    },
    async loadMonitor() {
      this.loadingMonitor = true;
      try {
        const [actions, contacts, webhooks] = await Promise.all([
          this.fetchHistory(ACTION_TYPES),
          this.fetchHistory(CONTACT_TYPES),
          this.fetchHistory(WEBHOOK_TYPES),
        ]);
        this.actionRows = actions;
        this.contactRows = contacts;
        this.webhookRows = webhooks;
        if (this.selectedMonitorId) {
          const stillVisible = [...actions, ...contacts, ...webhooks].some((item) => item.id === this.selectedMonitorId);
          if (!stillVisible) this.selectedMonitorId = null;
        }
      } catch (error) {
        console.error('Error loading activity monitor:', error);
      } finally {
        this.loadingMonitor = false;
      }
    },
    formatSyncType(value) {
      const labels = {
        action_started: 'Action started',
        action_executed: 'Action finished',
        contact_sync: 'Contact sync',
        webhook_monitor: 'Automatic check',
      };
      return labels[value] || String(value || '').replace(/_/g, ' ');
    },
    formatDirection(value) {
      if (value === 'ghl_to_sm8') return `${this.brand.agencyName} to ServiceM8`;
      if (value === 'sm8_to_ghl') return `ServiceM8 to ${this.brand.agencyName}`;
      return '—';
    },
    formatFriendlyDate(value) {
      if (!value) return 'Not yet';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
        window.addEventListener('message', ({ data }) => {
          if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
            resolve(data.payload);
          }
        });
      });
    },
  },
};
</script>

<style scoped>
.troubleshooting-tab {
  padding: 20px;
}

.panel {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  border-bottom: 2px solid #24ACFE;
  padding-bottom: 12px;
}

.panel-header h3 {
  margin: 0 0 6px;
  color: #2c3e50;
}

.panel-subtitle {
  margin: 0;
  color: #666;
  font-size: 13px;
  max-width: 620px;
  line-height: 1.45;
}

.panel-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.primary-btn,
.secondary-btn {
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 12px;
}

.primary-btn {
  background: #24ACFE;
  border: 1px solid #24ACFE;
  color: white;
}

.primary-btn:hover:not(:disabled) {
  background: #1b93db;
}

.secondary-btn {
  background: #eef6ff;
  border: 1px solid #b6d4fe;
  color: #1678c2;
}

.primary-btn:disabled,
.secondary-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.banner {
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
}

.banner-success {
  background: #e8f7ef;
  color: #146c43;
  border: 1px solid #b7e4c7;
}

.banner-error {
  background: #fdecea;
  color: #b42318;
  border: 1px solid #f5c2c0;
}

.banner-warn {
  background: #fff6e5;
  color: #8a5a00;
  border: 1px solid #f5d48b;
}

.banner-info {
  background: #eef6ff;
  color: #1678c2;
  border: 1px solid #b6d4fe;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.stat-card {
  background: #f7fbff;
  border: 1px solid #e4eef8;
  border-radius: 8px;
  padding: 14px;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
}

.stat-value.small {
  font-size: 15px;
}

.stat-value.ok {
  color: #146c43;
}

.stat-value.bad {
  color: #b42318;
}

.stat-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  word-break: break-word;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #24ACFE;
  color: white;
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
}

.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
  font-size: 13px;
}

.data-table tbody tr:nth-child(odd) {
  background: #f9f9f9;
}

.data-table tbody tr {
  cursor: pointer;
}

.data-table tbody tr.selected {
  background: #eef6ff;
}

.primary-text {
  color: #2c3e50;
  font-weight: 600;
}

.muted {
  color: #666;
  font-size: 12px;
  margin-top: 3px;
}

.nowrap {
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active,
.status-success {
  background: #e8f7ef;
  color: #146c43;
}

.status-inactive,
.status-missing {
  background: #fdecea;
  color: #b42318;
}

.empty,
.loading {
  color: #666;
  font-size: 14px;
  padding: 8px 0;
}

.monitor-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.monitor-tab {
  border: 1px solid #d9e2ec;
  background: white;
  color: #334e68;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.monitor-tab.active {
  background: #eef6ff;
  border-color: #24ACFE;
  color: #1678c2;
}

.tab-count {
  margin-left: 6px;
  color: #666;
  font-weight: 500;
}

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}

.filter-bar label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #666;
  font-weight: 600;
}

.filter-bar select,
.filter-bar input {
  border: 1px solid #d9e2ec;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  min-width: 140px;
  color: #2c3e50;
}

.filter-search {
  flex: 1;
  min-width: 220px;
}

.detail-panel {
  margin-top: 12px;
  padding: 12px;
  background: #f7fbff;
  border: 1px solid #e4eef8;
  border-radius: 8px;
}

.detail-line {
  margin: 6px 0 0;
  font-size: 13px;
  color: #334e68;
}

.hint {
  margin: 8px 0 12px;
  font-size: 12px;
  color: #666;
}

@media (max-width: 768px) {
  .panel-header {
    flex-direction: column;
  }
}
</style>
